<script setup lang="ts">
const chatMessages = [
  { sender: "employer", text: "Hi! We loved your video bio. Are you available for an interview tomorrow?", time: "10:32 AM" },
  { sender: "candidate", text: "Thank you! Yes, I'm free tomorrow afternoon.", time: "10:35 AM" },
  { sender: "employer", text: "Perfect! How about 2pm at our office?", time: "10:36 AM" },
];
</script>

<template>
  <div class="relative">
    <!-- Main Chat & Success Card -->
    <div class="rounded-3xl 3xl:rounded-[2rem] overflow-hidden shadow-warm-lg bg-white border border-sand/50">
      <!-- Header -->
      <div class="bg-gradient-to-r from-primary to-primary-light p-3 sm:p-5 2xl:p-6 3xl:p-8 4xl:p-10">
        <div class="flex items-center gap-2 sm:gap-3 2xl:gap-4">
          <div
            class="w-9 h-9 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-full bg-white overflow-hidden flex-shrink-0"
          >
            <div class="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <Icon
                name="material-symbols:domain"
                class="w-4 h-4 sm:w-6 sm:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-primary"
              />
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-white font-semibold text-sm sm:text-base 2xl:text-lg 3xl:text-xl 4xl:text-2xl block truncate"
              >Safari Lodge Resort</span
            >
            <span class="text-white/80 text-xs sm:text-sm 2xl:text-base 3xl:text-lg flex items-center gap-1 sm:gap-1.5">
              <span class="w-1.5 h-1.5 sm:w-2 sm:h-2 2xl:w-2.5 2xl:h-2.5 rounded-full bg-green-400"></span>
              Online
            </span>
          </div>
        </div>
      </div>

      <!-- Chat Content -->
      <div class="p-3 sm:p-4 2xl:p-6 3xl:p-8 4xl:p-10 space-y-2 sm:space-y-3 2xl:space-y-4 3xl:space-y-5 bg-cream/30">
        <div
          v-for="message in chatMessages"
          :key="message.time"
          class="flex"
          :class="message.sender === 'candidate' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] sm:max-w-[80%] p-2 sm:p-3 2xl:p-4 3xl:p-5 rounded-xl sm:rounded-2xl 2xl:rounded-3xl"
            :class="
              message.sender === 'candidate'
                ? 'bg-primary text-white rounded-br-sm sm:rounded-br-md'
                : 'bg-white shadow-warm rounded-bl-sm sm:rounded-bl-md'
            "
          >
            <p class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg">{{ message.text }}</p>
            <p
              class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base mt-0.5 sm:mt-1"
              :class="message.sender === 'candidate' ? 'text-white/70' : 'text-medium-gray'"
            >
              {{ message.time }}
            </p>
          </div>
        </div>

        <!-- Typing indicator -->
        <div class="flex justify-start">
          <div class="bg-white shadow-warm p-2 sm:p-3 2xl:p-4 rounded-xl sm:rounded-2xl rounded-bl-sm sm:rounded-bl-md">
            <div class="flex gap-0.5 sm:gap-1">
              <span
                class="w-1.5 h-1.5 sm:w-2 sm:h-2 2xl:w-2.5 2xl:h-2.5 rounded-full bg-medium-gray animate-bounce"
                style="animation-delay: 0ms"
              ></span>
              <span
                class="w-1.5 h-1.5 sm:w-2 sm:h-2 2xl:w-2.5 2xl:h-2.5 rounded-full bg-medium-gray animate-bounce"
                style="animation-delay: 150ms"
              ></span>
              <span
                class="w-1.5 h-1.5 sm:w-2 sm:h-2 2xl:w-2.5 2xl:h-2.5 rounded-full bg-medium-gray animate-bounce"
                style="animation-delay: 300ms"
              ></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Message Input -->
      <div class="p-3 sm:p-4 2xl:p-5 3xl:p-6 bg-white border-t border-sand pointer-events-none">
        <div class="flex items-center gap-2 2xl:gap-3">
          <div
            class="flex-1 h-8 sm:h-10 2xl:h-12 3xl:h-14 bg-cream rounded-lg sm:rounded-xl 2xl:rounded-2xl flex items-center px-2 sm:px-3 2xl:px-4"
          >
            <span class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg text-medium-gray">Type a message...</span>
          </div>
          <div
            class="w-8 h-8 sm:w-10 sm:h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-gold flex items-center justify-center flex-shrink-0"
          >
            <Icon name="material-symbols:send" class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-white" />
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Success Card -->
    <div
      class="hidden lg:flex absolute -right-4 lg:-right-8 2xl:-right-12 3xl:-right-16 top-2/4 z-20 bg-gradient-to-br from-savanna-dark to-savanna-light rounded-2xl 3xl:rounded-3xl shadow-mint p-4 2xl:p-5 3xl:p-6 animate-gentle-float"
      style="animation-delay: 0.3s"
    >
      <div class="flex flex-col items-center text-center">
        <div
          class="w-12 h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-full bg-white/20 flex items-center justify-center mb-2 2xl:mb-3"
        >
          <Icon name="material-symbols:celebration" class="w-6 h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-white" />
        </div>
        <p class="text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl font-bold text-white">Congrats!</p>
        <p class="text-xs 2xl:text-sm 3xl:text-base text-white/80">You got the job!</p>
      </div>
    </div>

    <!-- Floating Interview Badge -->
    <div
      class="hidden lg:flex absolute -left-4 lg:-left-8 2xl:-left-12 bottom-1/2 z-20 bg-white rounded-2xl 3xl:rounded-3xl shadow-warm-lg p-3 2xl:p-4 3xl:p-5 animate-gentle-float"
      style="animation-delay: 0.6s"
    >
      <div class="flex items-center gap-2 2xl:gap-3">
        <div
          class="w-10 h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 rounded-xl 2xl:rounded-2xl bg-secondary/10 flex items-center justify-center"
        >
          <Icon name="material-symbols:calendar-today" class="w-5 h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-secondary" />
        </div>
        <div>
          <p class="text-xs 2xl:text-sm 3xl:text-base text-medium-gray">Interview</p>
          <p class="text-sm 2xl:text-base 3xl:text-lg font-semibold text-dark-gray">Tomorrow, 2pm</p>
        </div>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -z-10 -top-8 -right-8 w-48 h-48 2xl:w-64 2xl:h-64 3xl:w-80 3xl:h-80 bg-savanna/10 rounded-full blur-3xl"></div>
  </div>
</template>
